/**
 * LiveProgressBar — Barra de progreso del evento en Modo En Vivo
 *
 * Muestra cuántas canciones de la lista personalizada han sido marcadas como
 * reproducidas, con barra de progreso visual y la última canción marcada.
 * Se integra con el Modo En Vivo existente (no crea un nuevo modo).
 *
 * Visible sólo cuando:
 *   - isLiveMode === true
 *   - currentPlaylist tiene lista_personalizada_generada con ≥1 canción
 *
 * Feature solicitada en Feb 2026 tras añadir marcado de reproducidas.
 * Reutiliza el estado existente `customListPlayedState` y la lista
 * `generatedCustomList` de App.js — cero duplicación de datos.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Music } from 'lucide-react';

const MILESTONES = [
  { threshold: 25, message: '✨ ¡Primera cuarta parte del evento!', emoji: '✨' },
  { threshold: 50, message: '🔥 ¡Mitad del evento completada!', emoji: '🔥' },
  { threshold: 75, message: '🚀 ¡Tres cuartas partes, recta final!', emoji: '🚀' },
  { threshold: 100, message: '🎉 ¡Lista completa! Evento redondo', emoji: '🎉' },
];

export const LiveProgressBar = ({ songs = [], playedState = {} }) => {
  const { total, playedCount, lastPlayed } = useMemo(() => {
    const total = songs.length;
    let playedCount = 0;
    let lastPlayed = null;
    let lastTimestamp = 0;
    for (const song of songs) {
      const entry = playedState[song.id];
      if (entry?.played) {
        playedCount += 1;
        const ts = entry.updated_at ? Date.parse(entry.updated_at) : 0;
        if (ts >= lastTimestamp) {
          lastTimestamp = ts;
          lastPlayed = song;
        }
      }
    }
    return { total, playedCount, lastPlayed };
  }, [songs, playedState]);

  const percent = total > 0 ? Math.round((playedCount / total) * 100) : 0;

  // Milestones: toast al cruzar 25/50/75/100%. Sólo cuando percent AUMENTA
  // (cruce ascendente) para no spamear al desmarcar o al cargar el evento.
  const prevPercentRef = useRef(null);
  useEffect(() => {
    if (total === 0) return;
    const prev = prevPercentRef.current;
    // Primera carga: establecemos ref sin notificar
    if (prev === null) {
      prevPercentRef.current = percent;
      return;
    }
    if (percent > prev) {
      for (const m of MILESTONES) {
        if (prev < m.threshold && percent >= m.threshold) {
          toast.success(m.message, { duration: 4000, id: `live-milestone-${m.threshold}` });
        }
      }
    }
    prevPercentRef.current = percent;
  }, [percent, total]);

  if (total === 0) return null;

  const remaining = total - playedCount;
  const lastTitle = lastPlayed?.titulo || lastPlayed?.title;
  const lastArtist = lastPlayed?.artista || lastPlayed?.artist;

  return (
    <div
      className="sticky top-2 z-30 mb-3 rounded-lg border border-red-500/40 bg-gradient-to-r from-red-950/90 via-slate-900/95 to-red-950/90 backdrop-blur-sm p-3 shadow-lg shadow-red-950/30"
      data-testid="live-progress-bar"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
          <span className="text-[10px] sm:text-xs uppercase tracking-wide text-red-300 font-semibold">
            En Vivo
          </span>
          <span className="text-white text-sm sm:text-base font-semibold ml-1" data-testid="live-progress-counter">
            {playedCount}<span className="text-slate-400">/{total}</span>
          </span>
          <span className="hidden sm:inline text-xs text-slate-400">
            canciones reproducidas
          </span>
        </div>
        <span
          className="text-emerald-300 text-sm font-bold tabular-nums flex-shrink-0"
          data-testid="live-progress-percent"
        >
          {percent}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden" data-testid="live-progress-fill-container">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500 ease-out"
          style={{ width: `${percent}%` }}
          data-testid="live-progress-fill"
        />
      </div>

      {/* Last played song */}
      {lastPlayed ? (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-300 truncate" data-testid="live-progress-last-played">
          <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
          <span className="text-slate-500 flex-shrink-0">Última:</span>
          <span className="truncate">
            <span className="text-white font-medium">{lastTitle}</span>
            {lastArtist && (
              <>
                <span className="text-slate-500 mx-1">·</span>
                <span className="text-slate-400">{lastArtist}</span>
              </>
            )}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500" data-testid="live-progress-empty">
          <Music className="w-3 h-3" />
          <span>Sin canciones marcadas aún · quedan {remaining}</span>
        </div>
      )}
    </div>
  );
};

export default LiveProgressBar;
